import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Table,
  Button,
  Modal,
  Spinner,
  Card,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import Swal from "sweetalert2";
import { API_URL } from "../../config";
import LoadingSpinner from "../../components/LoadingSpinner";
import { showToast } from "../../components/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { PermissionWrapper } from "../../helpers/permission.js";

const QUESTIONS_PER_PAGE = 100;

const AskExpertList = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const toastShownRef = useRef(false);

  // Search filter states
  const [subscriberFilter, setSubscriberFilter] = useState("");
  const [questionFilter, setQuestionFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Permission flags
  const canAdd = true;
  const canEdit = true;
  const canDelete = true;

  useEffect(() => {
    fetchQuestions();
  }, [currentPage]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      setError(null);

      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      };

      const response = await axios.get(
        `${API_URL}ask-expert?page=${currentPage}&limit=${QUESTIONS_PER_PAGE}`,
        config
      );

      // Fix: Check if the API returns data directly or nested in a data property
      const responseData = response.data;
      
      if (Array.isArray(responseData)) {
        // If the API returns an array directly
        setQuestions(responseData);
        setTotalQuestions(responseData.length);
      } else if (responseData.data && Array.isArray(responseData.data)) {
        // If the API returns data nested in a data property
        setQuestions(responseData.data);
        setTotalQuestions(responseData.total || responseData.data.length);
      } else {
        // Fallback if structure is different
        setQuestions([]);
        setTotalQuestions(0);
      }
      
      if (!toastShownRef.current) {
        showToast.success("Questions fetched successfully");
        toastShownRef.current = true;
      }
    } catch (err) {
      console.error("Error fetching questions:", err);
      setError(err.response?.data?.message || "Failed to fetch questions");
      setQuestions([]);
      if (!toastShownRef.current) {
        showToast.error("Failed to fetch questions");
        toastShownRef.current = true;
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter questions based on search criteria
  const filteredQuestions = useMemo(() => {
    return questions.filter((question) => {
      // Subscriber filter
      if (
        subscriberFilter &&
        !(question.t2_fullname || "").toLowerCase().includes(subscriberFilter.toLowerCase())
      ) {
        return false;
      }

      // Question filter
      if (
        questionFilter &&
        !(question.askquestion || "").toLowerCase().includes(questionFilter.toLowerCase())
      ) {
        return false;
      }

      // Status filter
      if (statusFilter) {
        const statusText = question.status === 1 ? "published" : "unpublished";
        if (!statusText.includes(statusFilter.toLowerCase())) {
          return false;
        }
      }

      return true;
    });
  }, [questions, subscriberFilter, questionFilter, statusFilter]);

  // Update pagination based on filtered results
  useEffect(() => {
    setTotalQuestions(filteredQuestions.length);
    setCurrentPage(1); // Reset to first page when filters change
  }, [filteredQuestions]);

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const config = {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            },
          };
          await axios.delete(`${API_URL}ask-expert/${id}`, config);
          Swal.fire("Deleted!", "The question has been deleted.", "success");
          fetchQuestions();
        } catch (err) {
          console.error("Error deleting question:", err);
          Swal.fire(
            "Error!",
            err.response?.data?.message || "Failed to delete question.",
            "error"
          );
        }
      }
    });
  };

  const showDetails = async (id) => {
    try {
      setSelectedId(id);
      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      };
      const response = await axios.get(
        `${API_URL}/ask-expert/${id}`,
        config
      );
      setModalContent(response.data);
      setShowModal(true);
    } catch (err) {
      console.error("Error fetching question details:", err);
      Swal.fire(
        "Error!",
        err.response?.data?.message || "Failed to load details.",
        "error"
      );
    }
  };

  const renderStatusBadge = (status) => {
    return status === 1 ? (
      <Button variant="primary" size="sm" className="btn-xs">
        Published
      </Button>
    ) : (
      <Button variant="danger" size="sm" className="btn-xs">
        Unpublished
      </Button>
    );
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= Math.ceil(totalQuestions / QUESTIONS_PER_PAGE)) {
      setCurrentPage(page);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const totalPages = Math.ceil(totalQuestions / QUESTIONS_PER_PAGE);
  const start = (currentPage - 1) * QUESTIONS_PER_PAGE + 1;
  const end = Math.min(currentPage * QUESTIONS_PER_PAGE, totalQuestions);
  const paginatedQuestions = filteredQuestions.slice(start - 1, end);

  if (loading && currentPage === 1) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "300px" }}
      >
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div id="content" className="">
      <Card className="with-rounded-corner with-shadow mt-5">
        <Card.Header className="panel-heading d-flex justify-content-between align-items-center">
          <h4 className="mt-2">Manage Ask Expert Questions</h4>
          {questions.length > 0 && (
            <div className="d-inline-block">
              <span style={{ fontSize: "0.8rem" }} className="me-2">
                {start} to {end} of {totalQuestions}
              </span>
              <button
                className="btn btn-default btn-sm"
                title="Previous"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              &nbsp;
              <button
                className="btn btn-default btn-sm"
                title="Next"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          )}
        </Card.Header>

        <Card.Body>
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {questions.length > 0 ? (
            <div className="table-responsive">
              <Table className="m-b-0">
                <thead>
                  <tr>
                    <th style={{ textAlign: "center" }}>#</th>
                    <th>Subscriber Name</th>
                    <th>Questions</th>
                    <th>Status</th>
                    <th style={{ width: "1%" }} colSpan="2">
                      Action
                    </th>
                  </tr>
                  <tr>
                    <th style={{ textAlign: "center" }}></th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search subscriber..."
                        value={subscriberFilter}
                        onChange={(e) => setSubscriberFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search question..."
                        value={questionFilter}
                        onChange={(e) => setQuestionFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      {/* <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search status..."
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      /> */}
                    </th>
                    <th colSpan="2">
                      <button
                        className="btn btn-default btn-sm"
                        onClick={() => {
                          setSubscriberFilter("");
                          setQuestionFilter("");
                          setStatusFilter("");
                        }}
                        title="Clear filters"
                      >
                        Clear
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedQuestions.length > 0 ? (
                    paginatedQuestions.map((question, index) => (
                      <tr key={question.id}>
                        <td
                          style={{ textAlign: "center", verticalAlign: "middle" }}
                        >
                          {(currentPage - 1) * QUESTIONS_PER_PAGE + index + 1}
                        </td>
                        <td>
                          {question.subscriberid ? (
                            <Link to={`/subscribers/${question.subscriberid}`}>
                              {question.t2_fullname || ""}
                            </Link>
                          ) : (
                            ""
                          )}
                        </td>
                        <td>{question.askquestion || "No question text"}</td>
                        <td>{renderStatusBadge(question.status)}</td>
                        <td>
                          {canEdit && (
                          <PermissionWrapper module="website_askexpert" action="edit">
                            <Button
                              variant="warning"
                              size="sm"
                              className="btn-xs"
                              onClick={() =>
                                navigate(
                                  `/Website/AskExpert/edit/${question.id}`
                                )
                              }
                              style={{ float: "left" }}
                            >
                              <FiEdit />
                            </Button>
                          </PermissionWrapper>
                          )}
                        </td>
                        <td>
                          {canDelete && (
                          <PermissionWrapper module="website_askexpert" action="delete">
                            <Button
                              variant="danger"
                              size="sm"
                              className="btn-xs"
                              onClick={() => handleDelete(question.id)}
                              style={{ float: "left" }}
                            >
                              <FiTrash2 />
                            </Button>
                          </PermissionWrapper>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">
                        No questions match your search criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-4">No questions found</div>
          )}
        </Card.Body>
      </Card>

      {/* Question Details Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Question Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalContent ? (
            <div>
              <div className="mb-3">
                <h5>Question:</h5>
                <p>{modalContent.askquestion || "No question text"}</p>
              </div>

              {modalContent.givenanswer && (
                <div className="mb-3">
                  <h5>Answer:</h5>
                  <p>{modalContent.givenanswer}</p>
                </div>
              )}

              <div className="row">
                <div className="col-md-6">
                  <p>
                    <strong>Status:</strong>{" "}
                    {modalContent.status === 1
                      ? "Published"
                      : "Unpublished"}
                  </p>
                </div>
                <div className="col-md-6">
                  <p>
                    <strong>Created At:</strong>{" "}
                    {formatDate(modalContent.created_on)}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AskExpertList;